import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  generateImage,
  getImageHistory,
  deleteImage,
  downloadImageFile,
  ImageHistoryItem
} from '../../services/imageService';
import PageHeader from '../common/PageHeader';
import './Images.css';

interface ImagesProps {
  onToggleSidebar: () => void;
  onClose: () => void;
}

const Images: React.FC<ImagesProps> = ({ onToggleSidebar, onClose }) => {
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'create' | 'history'>('create');
  const [prompt, setPrompt] = useState('');
  const [provider, setProvider] = useState<'gemini' | 'grok'>('gemini');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [imageHistory, setImageHistory] = useState<ImageHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deletingImages, setDeletingImages] = useState<Set<number>>(new Set());
  const [modalImage, setModalImage] = useState<{ url: string; prompt: string } | null>(null);

  // Load image history when user is authenticated
  useEffect(() => {
    if (!authLoading && user) {
      loadHistory();
    }
  }, [authLoading, user]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await getImageHistory(20, 0);
      if (response.success && response.data?.images) {
        setImageHistory(response.data.images);
      }
    } catch (err: any) {
      console.error('Failed to load image history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    if (prompt.length < 10) {
      setError('Prompt must be at least 10 characters long');
      return;
    }

    if (prompt.length > 500) {
      setError('Prompt must be less than 500 characters');
      return;
    }

    setError(null);
    setSuccess(null);
    setIsGenerating(true);

    try {
      const response = await generateImage(prompt, provider);

      if (response.success && response.data) {
        setSuccess('Image generated successfully!');
        setPrompt('');
        await loadHistory();
        setActiveTab('history');
      } else {
        setError(response.error || 'Failed to generate image');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleViewImage = (imageUrl: string, prompt: string) => {
    setModalImage({ url: imageUrl, prompt });
  };

  const handleCloseModal = () => {
    setModalImage(null);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setDeletingImages(prev => new Set(prev).add(id));
      await deleteImage(id);
      setSuccess('Image deleted successfully');
      loadHistory();
    } catch (err: any) {
      setError(err.message || 'Failed to delete image');
    } finally {
      setDeletingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleDownload = (imageUrl: string, prompt: string) => {
    const filename = `image_${Date.now()}.png`;
    downloadImageFile(imageUrl, filename);
    setSuccess('Image download started');
  };

  const charCount = prompt.length;

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="images-container">
        <div className="images-header">
          <h1 className="images-title">Image Generation</h1>
          <p className="images-subtitle">Loading...</p>
        </div>
      </div>
    );
  }

  // Show message if user is not authenticated
  if (!user) {
    return (
      <div className="images-container">
        <div className="images-header">
          <h1 className="images-title">Image Generation</h1>
          <p className="images-subtitle">Please sign in to generate images</p>
        </div>
        <div className="empty-state">
          <p>Authentication required to access image generation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="images-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/images</h1>}
      />
      
      <div className="images-content">
        <div className="images-header">
          <p className="images-subtitle">Create AI-generated images using Gemini or Grok</p>
        </div>

        {error && (
          <div className="images-message images-error">
            <span className="message-icon">⚠️</span>
            <span>{error}</span>
            <button className="message-close" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {success && (
          <div className="images-message images-success">
            <span className="message-icon">✓</span>
            <span>{success}</span>
            <button className="message-close" onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        <div className="images-tabs">
          <button
            className={`images-tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Create Image
          </button>
          <button
            className={`images-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        {activeTab === 'create' && (
          <div className="images-generator">
            <div className="generator-section">
              <label className="generator-label">
                <span className="label-text">AI Provider</span>
              </label>
              <div className="generator-provider-selector">
                <button
                  className={`generator-provider-btn ${provider === 'gemini' ? 'active' : ''}`}
                  onClick={() => setProvider('gemini')}
                  disabled={isGenerating}
                >
                  Gemini
                </button>
                <button
                  className={`generator-provider-btn ${provider === 'grok' ? 'active' : ''}`}
                  onClick={() => setProvider('grok')}
                  disabled={isGenerating}
                >
                  Grok
                </button>
              </div>
            </div>

            <div className="generator-section">
              <label className="generator-label">
                <span className="label-text">Image Prompt</span>
                <span className="label-info">{charCount}/500</span>
              </label>
              <textarea
                className="generator-textarea"
                placeholder="Describe the image you want to generate... (e.g., 'A serene beach at sunset with gentle waves')"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                maxLength={500}
                rows={4}
              />
            </div>

            <button
              className="generator-button"
              onClick={handleGenerate}
              disabled={isGenerating || prompt.length < 10}
            >
              {isGenerating ? 'Generating...' : 'Generate Image'}
            </button>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="images-section">
            <div className="section-header">
              <button className="refresh-button" onClick={() => loadHistory()} disabled={loadingHistory}>
                {loadingHistory ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {imageHistory.length === 0 && !loadingHistory && (
              <div className="empty-state">
                <p>No images generated yet</p>
              </div>
            )}

            {imageHistory.length > 0 && (
              <div className="images-grid">
                {imageHistory.map((image) => (
                  <div key={image.id} className="image-card">
                    <div className="image-wrapper" onClick={() => handleViewImage(image.image_url, image.prompt)}>
                      <img 
                        src={image.image_url} 
                        alt={image.prompt}
                        className="image-thumbnail"
                        loading="lazy"
                      />
                    </div>
                    <div className="image-info">
                      <p className="image-prompt">{image.prompt}</p>
                      <div className="image-meta">
                        <span className="image-date">{new Date(image.created_at).toLocaleDateString()}</span>
                        <div className="image-actions">
                          <button
                            className="image-action-btn"
                            onClick={() => handleDownload(image.image_url, image.prompt)}
                            title="Download"
                          >
                            ↓
                          </button>
                          <button
                            className="image-action-btn image-action-delete"
                            onClick={() => handleDelete(image.id)}
                            disabled={deletingImages.has(image.id)}
                            title="Delete"
                          >
                            {deletingImages.has(image.id) ? '...' : '×'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div className="image-modal-overlay" onClick={handleCloseModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={handleCloseModal}>×</button>
            <img src={modalImage.url} alt={modalImage.prompt} className="image-modal-image" />
            <p className="image-modal-prompt">{modalImage.prompt}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Images;

